import { supabase } from './supabaseClient';

export type Location = {
  id: string;
  name: string;
  organization_id: string;
  parent_id: string | null;
  parent?: Location | null;
  children?: Location[];
  path?: Location[]; // Array of parent locations from root to immediate parent
  level?: number; // Depth level in the hierarchy
};

export class LocationsClient {
  static async getLocationsByOrganizationId(
    organization_id: string
  ): Promise<Location[]> {
    const { data, error } = await supabase.rpc('get_locations_with_paths', {
      org_id: organization_id,
    });

    if (error) {
      throw error;
    }
    return data;
  }

  static async getLocationHierarchy(
    organization_id: string
  ): Promise<Location[]> {
    const { data, error } = await supabase
      .from('locations')
      .select(
        `
        *,
        parent:locations!parent_id (
          id,
          name,
          organization_id,
          parent_id
        )
      `
      )
      .eq('organization_id', organization_id)
      .order('name');

    if (error) {
      throw error;
    }

    // Build the hierarchy
    const locationMap = new Map<string, Location>();
    const roots: Location[] = [];

    // First pass: create all nodes
    data.forEach((location) => {
      const node: Location = {
        ...location,
        children: [],
        path: [],
        level: 0,
      };
      locationMap.set(location.id, node);
    });

    // Second pass: build the tree and calculate paths
    data.forEach((location) => {
      const node = locationMap.get(location.id);
      if (!node) return;

      if (location.parent_id) {
        const parent = locationMap.get(location.parent_id);
        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(node);
          // Calculate path and level
          node.path = this.buildLocationPath(node, locationMap);
          node.level = this.calculateLocationLevel(node, locationMap);
        }
      } else {
        roots.push(node);
        // Root locations have empty path array and level 0
        node.path = [];
        node.level = 0;
      }
    });

    return roots;
  }

  private static buildLocationPath(
    location: Location,
    locationMap: Map<string, Location>
  ): Location[] {
    const path: Location[] = [];
    let currentLocation = location;

    while (currentLocation.parent_id) {
      const parent = locationMap.get(currentLocation.parent_id);
      if (parent) {
        path.unshift(parent);
        currentLocation = parent;
      } else {
        break;
      }
    }

    return path;
  }

  private static calculateLocationLevel(
    location: Location,
    locationMap: Map<string, Location>
  ): number {
    let level = 0;
    let currentLocation = location;

    while (currentLocation.parent_id) {
      level++;
      const parent = locationMap.get(currentLocation.parent_id);
      if (parent) {
        currentLocation = parent;
      } else {
        break;
      }
    }

    return level;
  }

  static async createLocation(
    location: Omit<Location, 'id'>
  ): Promise<Location> {
    const { data, error } = await supabase
      .from('locations')
      .insert(location)
      .select();
    if (error) {
      throw new Error(error.message);
    }
    return data[0];
  }

  static async updateLocation(location: Location): Promise<Location> {
    const { data, error } = await supabase
      .from('locations')
      .update(location)
      .eq('id', location.id)
      .select();
    if (error) {
      throw error;
    }
    return data[0];
  }

  static async deleteLocation(id: string): Promise<Location> {
    const { data, error } = await supabase
      .from('locations')
      .delete()
      .eq('id', id)
      .select();
    if (error) {
      throw error;
    }
    return data[0];
  }
}
